<template>
  <section class="form-box-edit">
    <transition name="fadeInModal">
      <div
        class="form-box-edit__modal modal"
        :class="{'form-box-edit__modal--extend': isCustomInputsEnabled}"
        role="dialog"
        aria-labelledby="modalTitle"
        aria-describedby="modalDescription"
      >
        <!-- Header -->
        <div class="header">
          {{ initialEditModal ? "CREATE NEW PRODUCTS" : "EDITING PRODUCTS" }}
          <img
            v-if="!$isSingleLanguage"
            @click="
              () => {
                editingLanguage = $settingsDefaultLanguageType;
              }
            "
            class="header__flag-primary"
            :class="{
              header__activeLanguage:
                editingLanguage == $settingsDefaultLanguageType,
            }"
            :src="`https://www.countryflagicons.com/FLAT/64/${$settingsDefaultLanguageType}.png`"
            alt="def"
          />
          <img
            v-if="!$isSingleLanguage"
            @click="
              () => {
                editingLanguage = $settingsSecondLanguageType;
              }
            "
            class="header__flag-secondary"
            :class="{
              header__activeLanguage:
                editingLanguage == $settingsSecondLanguageType,
            }"
            :src="`https://www.countryflagicons.com/FLAT/64/${$settingsSecondLanguageType}.png`"
            alt="sec"
          />
          <div class="header__exit-button">
            <close-icon @closeEvent="btnCancel()" :dark="true" />
          </div>
        </div>
        <!-- <div class="header__secondary-header">
          Form Details
        </div> -->

        <!-- Color picker -->
        <div v-if="showTextColorPicker" class="color-picker">
          <div class="color-picker__inner">
            <chrome-picker v-model="colors[activeColorPicker]" />
            <button class="color-picker-button" v-on:click="closeColorPicker()">
              Close
            </button>
          </div>
        </div>

        <!-- Textfields -->
        <div class="textfield__group-double">
          <div class="textfield">
            <input
              class="textfield__input"
              type="email"
              style="width: 460px"
              v-model="selectedBoxInfo.email"
              :maxlength="titleMaxLength"
              required
            />
            <div class="textfield__label">EMAIL FOR RECEIVING ORDERS</div>
          </div>
          <div>
            <ButtonGlobal
              type="positive"
              text="CHANGE IMAGE"
              @eventPassed="showImageModal = true"
            />
            <ImageManager
              v-if="showImageModal"
              :isModal="true"
              @changed="onSelectedImage"
              @close="showImageModal = false"
            />
          </div>
        </div>
        <div class="textfield__group-double">
          <div class="textfield" v-on:click="showColorPicker('formColor')">
            <div
              class="textfield__color"
              v-bind:style="{ backgroundColor: selectedBoxInfo.formColor }"
            />
            <input
              class="textfield__input"
              pattern="^((0x){0,1}|#{0,1})([0-9A-Fa-f]{8}|[0-9A-Fa-f]{6})$"
              v-model="selectedBoxInfo.formColor"
              placeholder="Hex color... e.g #FFFFFF (white)"
              maxlength="7"
              required
            />
            <div class="textfield__label">BOX COLOR</div>
          </div>
          <div class="textfield" v-on:click="showColorPicker('textColor')">
            <div
              class="textfield__color"
              v-bind:style="{ backgroundColor: selectedBoxInfo.textColor }"
            />
            <input
              class="textfield__input"
              pattern="^((0x){0,1}|#{0,1})([0-9A-Fa-f]{8}|[0-9A-Fa-f]{6})$"
              v-model="selectedBoxInfo.textColor"
              placeholder="Hex color... e.g #FFFFFF (white)"
              maxlength="7"
              required
            />
            <div class="textfield__label">TEXT COLOR</div>
          </div>
        </div>
        <div class="textfield__fee">
          <GlobalInputSwitch
            label="INCLUDE SERVICE CHARGE?"
            v-model="isChargingForServiceEnabled"
          />
          <div class="textfield">
            <input
              class="textfield__input"
              :disabled="!isChargingForServiceEnabled"
              v-model="selectedBoxInfo.serviceCharge"
              onkeypress="return (event.charCode == 8 || event.charCode == 0 || event.charCode == 13) ? null : event.charCode >= 48 && event.charCode <= 57"
            />
            <div class="textfield__label">
              SERVICE CHARGE ( {{ selectedBoxInfo.currency }} )
            </div>
          </div>
        </div>
        
        <div class="textfield__group-double">
          <GlobalInputSwitch
            label="INCLUDE CUSTOM FORM?"
            v-model="isCustomInputsEnabled"
          />
          <div class="textfield">
            <input
              class="textfield__input"
              type="input"
              v-model="selectedBoxInfo.currency"
              :maxlength="currencyMaxLength"
            />
            <div class="textfield__label">DEFAULT CURRENCY</div>
          </div>
        </div>

        <!-- Custom Thank You Note -->

        <div class="textfield__custom-note" :class="{'textfield__custom-note--mb': !isCustomNoteEnabled}">
          <GlobalInputSwitch
            label="INCLUDE CUSTOM NOTE?"
            v-model="isCustomNoteEnabled"
          />
          <div class="textfield" v-if="isCustomNoteEnabled">
            <input
              class="textfield__input"
              type="input"
              v-if="secondLanguage"
              v-model="selectedBoxInfo.customNoteHeaderSec"
              :maxlength="headerMaxLength"
            />
            <input
              class="textfield__input"
              type="input"
              v-else
              v-model="selectedBoxInfo.customNoteHeader"
              :maxlength="headerMaxLength"
            />
            <div class="textfield__label">THANK YOU HEADER {{secondLanguage ? '(' + $settingsSecondLanguageType + ')' : '' }}</div>
          </div>
          <div class="textfield textfield--full-width" v-if="isCustomNoteEnabled">
            <input
              class="textfield__input"
              type="input"
              v-if="secondLanguage"
              v-model="selectedBoxInfo.customNoteDescriptionSec"
              :maxlength="descriptionMaxLength"
            />
            <input
              class="textfield__input"
              type="input"
              v-else
              v-model="selectedBoxInfo.customNoteDescription"
              :maxlength="descriptionMaxLength"
            />
            <div class="textfield__label">THANK YOU NOTE {{secondLanguage ? '(' + $settingsSecondLanguageType + ')' : '' }}</div>
          </div>
        </div>

        <!-- Form input creator -->
        <FormInputCreator
          :editingLanguage="editingLanguage"
          :existingForm="selectedBoxInfo"
          @updateForm="(updatedForm = $event), (newFormEdited = true)"
          v-if="isCustomInputsEnabled"
        />

        <!-- Form creator -->
        <FormCreator
          :editingLanguage="editingLanguage"
          :existingForm="selectedBoxInfo"
          @updateForm="(updatedForm = $event), (newFormEdited = true)"
        />

        <!-- BUTTON TEXT -->
        <div
          class="textfield__group-double"
          :style="{ display: secondLanguage ? 'none' : 'block' }"
        >
          <div class="textfield">
            <input
              class="textfield__input"
              type="text"
              v-model="selectedBoxInfo.buttonText"
              required
            />
            <div class="textfield__label">Button text</div>
          </div>
        </div>
        <div
          class="textfield__group-double"
          :style="{ display: secondLanguage ? 'block' : 'none' }"
        >
          <div class="textfield">
            <input
              class="textfield__input"
              type="text"
              v-model="selectedBoxInfo.buttonTextSec"
              required
            />
            <div class="textfield__label">Button text sec</div>
          </div>
        </div>

        <!-- Buttons -->
        <div class="box-body__buttons">
          <button-global
            class="box-button"
            type="cancel"
            text="CANCEL"
            @eventPassed="btnCancel()"
          ></button-global>
          <button-global
            class="box-button"
            type="positive"
            text="SAVE"
            @eventPassed="btnUpdate()"
          ></button-global>
        </div>

        <!-- Modals -->
        <transition name="fadeInModal">
          <confirm-modal
            @yes="closeWithYes()"
            @no="closeWithNo()"
            v-if="showConfirmModal"
          >
            <h2 slot="title">Delete Box?</h2>
            <p slot="description">
              Cancelling at this stage will delete this box
            </p>
          </confirm-modal>
        </transition>
      </div>
    </transition>

    <!-- Error blocks -->
    <!-- Commented out for now because of positioning f!cking up. Integrate these in textfield components for maximum happiness :)
    For now, it still marks textfield red in case of error -->
    <!-- <input class="textfield__input" type="email" v-model="selectedBoxInfo.email" style="display:none">
    <div class="textfield__input__error">
    <div class="textfield__arrow"></div>
      This doesn’t look like a valid email address
    </div>
    <input class="textfield__input" pattern='^((0x){0,1}|#{0,1})([0-9A-Fa-f]{8}|[0-9A-Fa-f]{6})$' v-model="selectedBoxInfo.textColor" style="display:none">
    <div class="textfield__input__error-color">
    <div class="textfield__arrow"></div>
      This doesn’t look like a valid color
    </div>
    <input class="textfield__input" pattern='^((0x){0,1}|#{0,1})([0-9A-Fa-f]{8}|[0-9A-Fa-f]{6})$' v-model="selectedBoxInfo.formColor" style="display:none">
    <div class="textfield__input__error-color-2">
    <div class="textfield__arrow"></div>
      This doesn’t look like a valid color
    </div> -->
  </section>
</template>

<script>
import CloseIcon from "@/components/GlobalComponents/CloseIcon";
import ButtonGlobal from "@/components/GlobalComponents/ButtonGlobal";
import ConfirmModal from "@/components/GlobalComponents/ConfirmModal";
import ImageManager from "@/components/MediaPage/MediaImages";
import GlobalInputSwitch from "@/components/GlobalComponents/GlobalInputSwitch";
import FormCreator from "./FormCreator";
import FormInputCreator from "./FormInputCreator";

import languageCookieMixin from "@/mixins/languageCookieMixin";
import generateUuidMixin from "@/mixins/generateUuidMixin";
import { db, firestore } from "@/firebase";
import { Sketch } from "vue-color";
import bus from "@/bus.js";

export default {
  components: {
    "chrome-picker": Sketch,
    GlobalInputSwitch,
    CloseIcon,
    ButtonGlobal,
    FormCreator,
    FormInputCreator,
    ConfirmModal,
    ImageManager,
  },
  mixins: [languageCookieMixin, generateUuidMixin],
  props: {
    box: Object,
    completeBoxArray: Array,
    selectedBoxIndex: Number,
    initialEditModal: Boolean,
  },
  data() {
    return {
      selectedBoxInfo: Object.assign({}, this.box),
      titleMaxLength: 30,
      headerMaxLength: 15,
      descriptionMaxLength: 90,
      currencyMaxLength: 3,
      colors: {
        formColor: {
          hex: "#194d33",
        },
        textColor: {
          hex: "#194d33",
        },
      },
      showTextColorPicker: false,
      activeColorPicker: "",
      selectedForm: null,
      selectedLanguage: "none",
      editingLanguage: this.$settingsDefaultLanguageType,
      step: 0,
      formOption: "New",
      newFormEdited: false,
      updatedForm: {},
      showConfirmModal: false,
      showImageModal: false,
      selectedImage: "",
      isChargingForServiceEnabled: false,
      isCustomInputsEnabled: false,
      isCustomNoteEnabled: false,
    };
  },
  computed: {
    secondLanguage() {
      return (
        !this.$isSingleLanguage &&
        this.editingLanguage === this.$settingsSecondLanguageType
      );
    },
  },
  watch: {
    colors: {
      handler() {
        this.selectedBoxInfo[this.activeColorPicker] = this.colors[
          this.activeColorPicker
        ].hex;
      },
      deep: true,
    },
  },
  created() {
    if (this.getCookie("secondLanguage")) {
      this.selectedLanguage = this.$settingsSecondLanguageType;
    } else {
      this.selectedLanguage = this.$settingsDefaultLanguageType;
    }

    this.isChargingForServiceEnabled =
      this.selectedBoxInfo.serviceCharge > 0 ? true : false;
    this.isCustomInputsEnabled = 
      this.selectedBoxInfo.isCustomInputsEnabled ? true : false;
    this.isCustomNoteEnabled = 
      this.selectedBoxInfo.isCustomNoteEnabled ? true : false;
  },
  mounted: function () {
    document.addEventListener("keydown", this.hideflow);
  },
  destroyed: function () {
    document.removeEventListener("keydown", this.hideflow);
  },
  methods: {
    isValid() {
      let valid = true;
      let els = document.getElementsByClassName("textfield__input");
      Array.prototype.forEach.call(els, function (v) {
        if (!v.checkValidity()) {
          valid = false;
          return;
        }
      });

      return valid;
    },
    hideflow() {
      bus.$emit("bodyOverflow", true);
    },
    closeWithNo() {
      if (this.showConfirmModal) {
        this.showConfirmModal = false;
      }
    },
    closeWithYes() {
      this.$emit("deleteUneditedForm");
      this.$emit("formModalEditable");
      bus.$emit("bodyOverflow", false);
    },
    charactersRemaining(string) {
      if (string) {
        return string.length;
      } else {
        return 0;
      }
    },
    close() {
      this.$emit("close");
      this.showTextColorPicker = false;
      this.showDropdown = false;
    },
    closeColorPicker() {
      this.showTextColorPicker = false;
    },
    showColorPicker(activeBox) {
      this.showTextColorPicker = true;
      this.activeColorPicker = activeBox;
      this.colors[activeBox].hex = this.selectedBoxInfo[activeBox];
    },
    btnUpdate() {
      let invalid = this.isValid() ? false : true;
      let message = "Invalid input";
      
      this.selectedBoxInfo.isCustomInputsEnabled = this.isCustomInputsEnabled;
      this.selectedBoxInfo.isCustomNoteEnabled = this.isCustomNoteEnabled;

      if (this.selectedBoxInfo.title.trim().length == 0) {
        message = "You are missing a title";
        invalid = true;
      } else if (this.selectedBoxInfo.categories.length == 0) {
        message = "You have not created any categories";
        invalid = true;
      } else if (this.selectedBoxInfo.categories.length > 0) {
        this.selectedBoxInfo.categories.forEach((v) => {
          if (v.items.length == 0 || v.title.length == 0) {
            message = "One of your categories have no title or no items in it";
            invalid = true;
          }
        });
      }

      if(this.selectedBoxInfo.isCustomInputsEnabled && this.selectedBoxInfo.inputs.length == 0) {
        message = "You have not created any inputs";
        invalid = true;
      } else if (this.selectedBoxInfo.categories.length > 0) {
        this.selectedBoxInfo.categories.forEach((v) => {
          if (v.items.length == 0 || v.title.length == 0) {
            message = "One of your categories have no title or no items in it";
            invalid = true;
          }
        });
      }

      if (invalid) {
        this.$toastEvents.push({
          type: "negative",
          title: "Error",
          content: message,
          id: +new Date(),
        });
        return;
      }

      let initialPromise = true;

      if (this.initialEditModal) {
        let idOfTheForm = this.generateUUID();
        this.selectedBoxInfo.id = idOfTheForm;
        initialPromise = firestore
          .collection("FormStatistics")
          .doc(`${this.$hotelId}`)
          .collection("Forms")
          .doc(this.selectedBoxInfo.id)
          .set({
            formTitle: this.selectedBoxInfo.title,
            formTitleSec: this.selectedBoxInfo.titleSec,
            totalRevenue: 0,
            items: [],
            visibility: ["precheckin", "checkin", "anonymous"],
            created: new Date().getTime(),
          });
        this.selectedBoxInfo.visibility = {
          precheckin: true,
          checkin: true,
          anonymous: true,
        };
      } else {
        firestore
          .collection("FormStatistics")
          .doc(`${this.$hotelId}`)
          .collection("Forms")
          .doc(this.selectedBoxInfo.id)
          .update({
            formTitle: this.selectedBoxInfo.title,
            formTitleSec: this.selectedBoxInfo.titleSec,
          });
      }

      //set visibilty and service charge
      this.selectedBoxInfo.serviceCharge = this.isChargingForServiceEnabled
        ? Number(this.selectedBoxInfo.serviceCharge)
        : 0;

      this.completeBoxArray[this.selectedBoxIndex] = this.selectedBoxInfo;
      let secPromise = db
        .ref(`hotels/${this.$customerId}/${this.$hotelId}/boxes/`)
        .set(this.completeBoxArray);

      Promise.all([secPromise, initialPromise]).then(() => {
        this.$emit("formModalEditable");
        this.close();

        if (this.initialEditModal) {
          this.$emit("showVisibilityModal");
        }
        this.$toastEvents.push({
          type: "positive",
          title: "Success",
          content: "Updates to products saved",
          id: +new Date(),
        });
      });
    },
    buttonNext() {
      this.step = 1;
    },
    buttonBack() {
      this.step = 0;
    },
    btnCancel() {
      if (this.initialEditModal) {
        this.showConfirmModal = true;
      } else {
        this.close();
      }
    },
    onSelectedImage(imageUrl) {
      this.selectedBoxInfo.backgroundImage = imageUrl;
    },
  },
};
</script>

<style scoped>
@supports (-ms-ime-align: auto) {
  .form-box-edit {
    overflow: hidden;
  }
}

/*
    Box
  */

* {
  box-sizing: border-box;
  margin: 0px;
  padding: 0px;
}

.form-box-edit {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.8);
}

.form-box-edit__modal {
  position: relative;
  margin: 20px auto;
  border-radius: 8px;
  width: 660px;
  height: 1000px;
  min-height: 797px;
  background: #ffffff;
  overflow-x: auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 30px 25px 0px;
}

.form-box-edit__modal--extend {
  height: 1400px;
}

.modal {
  overflow: hidden;
}

/*
    Header
  */

.header {
  display: flex;
  align-items: center;
  font-size: 32px;
  color: #707070;
  margin-bottom: 25px;
}

.header__secondary-header {
  display: flex;
  font-size: 25px;
  margin-bottom: 25px;
  color: #707070;
}

.header__exit-button {
  position: absolute;
  top: 0px;
  right: 5px;
}

.header__flag-primary,
.header__flag-secondary {
  cursor: pointer;
  width: 40px;
  margin-left: 30px;
}

.header__activeLanguage {
  border-bottom: 2px solid #48b9ba;
}

.image-picker {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  margin-bottom: 24px;
}

/*
    Textfields
  */

.color-picker-button {
  padding: 0;
  min-width: 0;
}

.textfield__input {
  display: flex;
  width: 100%;
  height: 50px;
  padding-left: 14px;
  font-size: 20px !important;
  border: 2px solid #aaaaaa;
  box-sizing: border-box;
  border-radius: 8px;
  background-image: none;
  background-color: transparent;
  -webkit-box-shadow: none;
  -moz-box-shadow: none;
  box-shadow: none;
}

.textfield__color ~ .textfield__input {
  cursor: pointer;
}

.textfield__input:focus {
  outline: none;
  border: 2px solid #48b9ba;
}

.textfield__input:invalid {
  border: 2px solid #d95d5d;
}

.textfield__input__error,
.textfield__input__error-color,
.textfield__input__error-color-2 {
  display: none;
}

.textfield__input:invalid + .textfield__input__error,
.textfield__input:invalid + .textfield__input__error-color,
.textfield__input:invalid + .textfield__input__error-color-2 {
  position: absolute;
  display: flex;
  align-items: center;
  width: 220px;
  height: 50px;
  top: 86px;
  right: 390px;
  margin: 25px 0;
  padding-right: 10px;
  font-size: 14px;
  color: #fff;
  background: #d95d5d;
}

.textfield__input:invalid + .textfield__input__error-color {
  top: 235px;
  right: 390px;
}

.textfield__input:invalid + .textfield__input__error-color-2 {
  top: 160px;
  right: 390px;
}

.textfield__arrow {
  position: relative;
  right: 25px;
  width: 0;
  height: 0;
  border-top: 25px solid transparent;
  border-bottom: 25px solid transparent;
  border-right: 25px solid #d95d5d;
}

.textfield__label {
  font-size: 16px;
  position: absolute;
  color: #48b9ba;
  background-color: #fff;
  padding: 0 5px;
  margin-left: 10px;
  bottom: -10px;
}

.textfield__input:invalid ~ .textfield__label {
  color: #d95d5d;
}

.textfield {
  width: 100%;
  height: 70px;
  background: #ffffff;
  margin-bottom: 25px;
  display: flex;
  flex-direction: column-reverse;
  position: relative;
}

.textfield__color {
  border: none;
  background-image: none;
  outline: none;
  background-color: transparent;
  -webkit-box-shadow: none;
  -moz-box-shadow: none;
  box-shadow: none;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  position: absolute;
  right: 8px;
  top: 50%;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.25);
  cursor: pointer;
}

.textfield__group-double,
.textfield__custom-note,
.textfield__fee {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.textfield__group-double .textfield,
.textfield__custom-note .textfield,
.textfield__fee .switch,
.textfield__fee .textfield {
  width: 295px;
}

.textfield__fee {
  color: #48b9ba;
}
.textfield__custom-note {
  flex-wrap: wrap;
} 
.textfield__custom-note--mb {
  margin-bottom: 15px;
}
.textfield__custom-note .textfield--full-width {
  width: 100%;
  height: 50px;
}

.textfield__fee .switch {
  display: flex;
  align-items: center;
}

.textfield__group-double .textfield:first-child {
  margin-right: 20px;
}

.textfield input:disabled {
  color: #aaaaaa;
}
.textfield input:disabled ~ .textfield__label {
  color: #aaaaaa;
}

/*
    Buttons
  */

.box-button {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 14px;
}

.box-body__buttons {
  margin-top: 20px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

/*
    Color picker
  */

.color-picker {
  position: absolute;
  top: 100px;
  right: 100px;
  z-index: 99;
}

.color-picker__inner {
  position: relative;
}

.color-picker__inner button {
  position: absolute;
  bottom: 5px;
  left: 15px;
  background-color: #48b9ba;
  color: #fff;
  cursor: pointer;
  width: 165px;
  height: 25px;
  border: none;
  border-radius: 5px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.25);
  transition: 0.1s ease-in;
}

.color-picker__inner button:hover {
  background-color: #49a39c;
}

.color-picker__inner button.color-picker__exit {
  left: 85px;
  background-color: #894e99;
}

/* ??? */

.box-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  padding: 0 0 0 31px;
}

.box-editor__row {
  display: flex;
  align-items: center;
  width: 100%;
  margin: 5px 0px;
}

@media screen and (max-width: 765px) {
  .modal {
    width: 90%;
  }
  .box-editor,
  .form-editor {
    padding: 10px 0px;
  }
  .selectionWrap {
    width: 100%;
    margin-bottom: 30px;
  }
  h2.headerWrap {
    margin: 10px 0px;
    font-size: 24px;
  }
  .labelWrap {
    width: 100%;
  }
  .textfield,
  div.mediaWrapper {
    width: 80%;
  }
}

@media screen and (min-width: 2000px) {
  .textfield__input:invalid + .textfield__input__error,
  .textfield__input:invalid + .textfield__input__error-color,
  .textfield__input:invalid + .textfield__input__error-color-2 {
    right: 670px;
  }
}
</style>
